import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface VirtualRange {
  endIndex: number;
  offsetTop: number;
  startIndex: number;
  totalHeight: number;
}

interface UseLeadsRegistryCardsVirtualScrollParams<TItem> {
  itemHeight: number;
  items: TItem[];
  overscan: number;
  viewportHeight: number;
}

const getVirtualRange = ({
  itemCount,
  itemHeight,
  overscan,
  scrollTop,
  viewportHeight,
}: {
  itemCount: number;
  itemHeight: number;
  overscan: number;
  scrollTop: number;
  viewportHeight: number;
}): VirtualRange => {
  const totalHeight = itemCount * itemHeight;
  const visibleRowsCount = Math.ceil(viewportHeight / itemHeight);
  const maxStartIndex = Math.max(0, itemCount - visibleRowsCount);
  const startIndex = Math.min(
    maxStartIndex,
    Math.max(0, Math.floor(scrollTop / itemHeight) - overscan)
  );
  const endIndex = Math.min(
    itemCount,
    startIndex + visibleRowsCount + overscan * 2
  );

  return {
    endIndex,
    offsetTop: startIndex * itemHeight,
    startIndex,
    totalHeight,
  };
};

const isSameRange = (prev: VirtualRange, next: VirtualRange) =>
  prev.startIndex === next.startIndex &&
  prev.endIndex === next.endIndex &&
  prev.offsetTop === next.offsetTop &&
  prev.totalHeight === next.totalHeight;

export const useLeadsRegistryCardsVirtualScroll = <TItem>({
  itemHeight,
  items,
  overscan,
  viewportHeight,
}: UseLeadsRegistryCardsVirtualScrollParams<TItem>) => {
  const scrollTopRef = useRef(0);

  const calculateRange = useCallback(
    (scrollTop: number) =>
      getVirtualRange({
        itemCount: items.length,
        itemHeight,
        overscan,
        scrollTop,
        viewportHeight,
      }),
    [itemHeight, items.length, overscan, viewportHeight]
  );

  const [range, setRange] = useState<VirtualRange>(() => calculateRange(0));

  useEffect(() => {
    setRange((prev) => {
      const next = calculateRange(scrollTopRef.current);

      return isSameRange(prev, next) ? prev : next;
    });
  }, [calculateRange]);

  const updateScrollTop = useCallback(
    (scrollTop: number) => {
      scrollTopRef.current = scrollTop;

      setRange((prev) => {
        const next = calculateRange(scrollTop);

        return isSameRange(prev, next) ? prev : next;
      });
    },
    [calculateRange]
  );

  const visibleItems = useMemo(
    () => items.slice(range.startIndex, range.endIndex),
    [items, range.endIndex, range.startIndex]
  );

  return {
    range,
    updateScrollTop,
    visibleItems,
  };
};
